/**
 * Runtime feature flags / maintenance kill-switches — the identity domain's `FeatureFlag` aggregate.
 *
 * At most one live row per registry key (see `pages/feature-flags/registry.ts`); a key with no row
 * evaluates to its code default. Each platform has a mode: `on` (every build), `off` (no build) or
 * `builds` (only builds inside the inclusive `[min, max]` range, a null bound being unbounded).
 * Builds are integers (iOS `CFBundleVersion`, Android `versionCode`).
 */

/** The modes the console writes. The projection decodes modes as strings (see {@link FeatureFlagDoc}). */
export type FeatureFlagMode = "on" | "off" | "builds";

export const FEATURE_FLAG_MODES: readonly FeatureFlagMode[] = ["on", "off", "builds"];

export function isFeatureFlagMode(value: unknown): value is FeatureFlagMode {
  return typeof value === "string" && (FEATURE_FLAG_MODES as readonly string[]).includes(value);
}

/**
 * The Firestore projection (`featureFlags/{id}`, contract v1 — frozen, additive only). Readable
 * by anyone, before sign-in, so it is REDACTED: operator prose (`reason`) and the operator id
 * (`updatedBy`) are never projected — see {@link FeatureFlagRecord} for the admin read.
 *
 * Modes are typed as plain strings on purpose: a mode added later than this console is shown as
 * unknown and evaluates as `off` for that platform only, exactly as the apps and the API do.
 */
export interface FeatureFlagDoc {
  id: string;
  /** Registry key. Immutable. */
  key: string;
  /** `on` | `off` | `builds` (any other value is treated as `off`). */
  iosMode: string;
  /** Inclusive lower bound, only with `builds`. Null = unbounded. */
  iosMinBuild: number | null;
  /** Inclusive upper bound, only with `builds`. Null = unbounded. */
  iosMaxBuild: number | null;
  androidMode: string;
  androidMinBuild: number | null;
  androidMaxBuild: number | null;
  /** User-facing operator copy shown while the feature is unavailable. Public — plain text only. */
  message: string | null;
  /** Display only (and the API's `Retry-After`); nothing switches back on automatically. */
  expectedBackAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Always null — "back to default" is a write, never a delete. */
  deletedAt: string | null;
}

/** The full row returned by the admin `GET`/`POST`/`PATCH` — adds the audit fields. */
export interface FeatureFlagRecord extends FeatureFlagDoc {
  /** Why the last change was made. Audit only; never projected. */
  reason: string;
  /** Backend `User.id` of the operator who made the last change. Audit only; never projected. */
  updatedBy: string;
}
